import { useMutation, useQuery } from '@apollo/client/react';
import {
  OPTICLOCK_PROJECTS,
  OPTICLOCK_SET_PROJECT_ARCHIVED,
  OPTICLOCK_UPSERT_PROJECT,
} from '@staff-app/graphql-client';
import type { OpticlockProject, OpticlockProjectInput } from '@staff-app/shared-types';

/**
 * Root's project-category CRUD.
 *
 * There is deliberately NO delete hook: entries reference a project forever, so archiving is
 * the only removal and the API offers nothing else.
 */

/** Root's admin table wants the archived rows too — hence includeArchived: true. */
export function useOpticlockAllProjects() {
  return useQuery<{ opticlockProjects: OpticlockProject[] }>(OPTICLOCK_PROJECTS, {
    variables: { includeArchived: true },
    fetchPolicy: 'cache-and-network',
  });
}

/**
 * Create (no id → the server slugifies the name) or rename/recolour (id → display fields only;
 * the slug is immutable because the external consumer keys on it).
 *
 * Refetches the dashboard as well as the list: the rollup's tooltip carries project NAMES, so a
 * rename that skipped it would leave the old label on screen.
 */
export function useUpsertOpticlockProject() {
  return useMutation<{ opticlockUpsertProject: OpticlockProject }, { input: OpticlockProjectInput }>(
    OPTICLOCK_UPSERT_PROJECT,
    { refetchQueries: ['opticlockProjects', 'opticlockAgentMonthTotals'] },
  );
}

export function useSetOpticlockProjectArchived() {
  return useMutation<
    { opticlockSetProjectArchived: OpticlockProject },
    { id: string; archived: boolean }
  >(OPTICLOCK_SET_PROJECT_ARCHIVED, { refetchQueries: ['opticlockProjects'] });
}
